/**
 * Live logs for the fleet: a center's Orthanc, the shared Caddy, or an edge
 * node. Pick a source and stream it; the tail follows until you stop it.
 *
 * Reading logs needs the operator role and the `logs_enabled` switch; an edge
 * stream also needs the terminal enabled, since it rides the same SSH path. Each
 * unmet condition surfaces as the ticket request's error rather than a hidden
 * control.
 *
 * ── What the redesign changed, and why ──────────────────────────────────────
 *
 * Carbon is gone; the page is now a picker rail beside a dense, calm tail. The
 * substantive changes are all about not overstating what a log line is.
 *
 * The socket carries **unstructured text**. There is no per-line level, no
 * per-line timestamp field, no center, no retention and no server-side search.
 * Container sources run with `--no-log-prefix`, so those lines carry no
 * timestamp at all; edge sources run with `--timestamps`, so the timestamp is
 * *inside* the text. That rules out the log-viewer everyone reaches for — a
 * level column, a time column, a date-range over history, a severity-over-time
 * summary — because every one of those would be a column the data cannot fill.
 * So there is no table here: there are lines.
 *
 * What the page does offer is a severity *highlight*, computed in this browser
 * by matching the line (Orthanc's leading `E`/`W`, or INFO/WARN/ERROR in the
 * text), plus a filter over that match. It is drawn quietly — a 14% wash and a
 * 2px bar, with a letter in the gutter so it is not colour alone — precisely so
 * it does not read as authoritative as a StatusBadge does. The footnote under
 * every tail says it is a guess, in those words.
 *
 * `since`/`until` are new. They are docker's own grammar (RFC3339, or an offset
 * like `2h`), passed through untouched, and the field validation mirrors the
 * controller's request pattern exactly rather than inventing a second parser.
 * The helper text says what the controller's own comment says: a window narrows
 * the *replay*, never the follow.
 *
 * Download saves the buffer **this browser is holding**, and says so. Nothing is
 * retained controller-side, so a server-side export would be a promise the
 * system cannot keep.
 *
 * One deviation worth flagging. This page no longer renders
 * `components/LogStream`: that component's props cannot carry `since`/`until`
 * and it appends raw text nodes into its own `<pre>`, so it can neither open the
 * window the operator now asks for nor hand back the buffer that the severity
 * filter and Download need. Editing it was out of scope (it is shared, and it
 * still imports Carbon), so the tail lives here instead — reusing `apiPost`,
 * `websocketUrl` and `describeError`, which is where the actual ticket-then-
 * socket protocol lives. If `LogStream` later grows `since`, `until` and an
 * `onLine` callback, `LogTail` below should collapse back into it.
 *
 * ── the compaction pass ─────────────────────────────────────────────────────
 *
 * Same ticket, same socket, same states. What changed is that the tail is now
 * the page rather than two thirds of it:
 *
 *   The picker rail is gone. Source, tail, since and until are one
 *   toolbar row above the stream, so the viewer gets the full content width
 *   instead of sharing it with a 21rem column that is read once per session.
 *   `page-logs.css` then gives the stream `calc(100vh - chrome)`, floored at
 *   24rem, so on any real screen the lines fill it.
 *
 *   The field-by-field helper text went with it. What survived is what a wrong
 *   value actually costs: the docker grammar for since/until and the fact that a
 *   window narrows the replay and not the follow, both on one line under the
 *   row; and the SSH condition on an edge source, which is the difference
 *   between "no output" and "not permitted". `invalidText` is untouched.
 *
 * ── no typed edge address ───────────────────────────────────────────────────
 *
 *   An edge stream used to SSH to whatever address was typed here, with no
 *   host-key check. The server now derives the node's mesh address itself (the
 *   owner's NetBird link, else the agent's report) and accepts only the host
 *   key pinned for that node — the same rule as the terminal. The field is
 *   gone, and the address the server chose comes back on the ticket and is
 *   shown in the stream's facts, so the operator still sees where the lines
 *   are coming from.
 *
 *   Copy visible / Copy all loaded are new, and they are exact: this page owns
 *   the buffer, so "visible" is the severity-filtered list actually rendered and
 *   "all loaded" is `lines` — which is capped at MAX_LINES, the same cap
 *   Download has always had and the footnote has always stated. Neither button
 *   can offer more than the browser still holds, because nothing is retained
 *   controller-side. Download stays, demoted to ghost beside them; Clear is the
 *   only destructive control here and is the only one drawn as one.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from 'react';

import { Callout } from '../components/Callout';
import { Freshness } from '../components/Freshness';
import { PageHeader } from '../components/PageHeader';
import {
  EmptyBlock,
  ErrorBlock,
  LoadingBlock,
  SkeletonRows,
  StaleBanner,
} from '../components/States';
import { Button } from '../components/ui/Button';
import { Card, CardHeader } from '../components/ui/Card';
import { CopyButton } from '../components/ui/CopyButton';
import { SegmentedFilter } from '../components/ui/FilterBar';
import {
  NumberInput,
  Select,
  SelectItem,
  SelectItemGroup,
  TextInput,
  Toggle,
} from '../components/ui/Form';
import {
  ArrowDownIcon,
  DownloadIcon,
  TerminalIcon,
  TrashIcon,
} from '../components/ui/Icon';
import { Tooltip } from '../components/ui/Tooltip';
import { apiPost, describeError, websocketUrl } from '../lib/api';
import { formatNumber, pluralise } from '../lib/format';
import { useResource } from '../lib/useResource';
import type {
  LogSourceListResponse,
  LogTicketRequest,
  LogTicketResponse,
} from '../types/api';

import '../styles/page-logs.css';

const TAIL_MIN = 1;
const TAIL_MAX = 2000;

/**
 * The controller validates `since`/`until` with exactly this pattern and then
 * hands the string to `docker logs` unchanged. Mirroring it here turns a 422
 * into an inline message; deviating from it would invent a second grammar, so
 * the two are kept character-for-character identical.
 */
const WINDOW_PATTERN = /^$|^[0-9TZ:.+\-]{1,32}$|^[0-9]+[smhd]$/;

/** Above this many lines the oldest are dropped — a bound on a tail left
 *  running for hours. The number is quoted to the operator in the footnote,
 *  because a buffer that silently forgets is a buffer that lies. */
const MAX_LINES = 4000;

/** Lines are batched into state on this cadence rather than per frame: a busy
 *  container emits far faster than React wants to re-render. */
const FLUSH_MS = 120;

/* ── client-side severity, and nothing more ────────────────────────────────
   Every one of these is a guess about text. Orthanc writes glog-style lines
   (`E0823 09:14:02.118 main.cpp:88] ...`); on an edge source docker's own
   RFC3339 stamp sits in front of it, so the marker may be the second token.
   The word tests are deliberately case-SENSITIVE: log levels are conventionally
   upper-case, and a case-insensitive `\berror\b` would paint "0 errors" red. */

type LineSeverity = 'error' | 'warn' | 'plain';

const ORTHANC_MARKER = /(?:^|\s)([EW])\d{4}\s/;
const TEXT_ERROR = /\b(?:ERROR|ERR|CRITICAL|FATAL|PANIC|SEVERE)\b|Traceback \(most recent call last\)/;
const TEXT_WARN = /\b(?:WARN|WARNING)\b/;

function classify(line: string): LineSeverity {
  const marker = ORTHANC_MARKER.exec(line);
  if (marker) return marker[1] === 'E' ? 'error' : 'warn';
  if (TEXT_ERROR.test(line)) return 'error';
  if (TEXT_WARN.test(line)) return 'warn';
  return 'plain';
}

const SEVERITY_MARK: Record<LineSeverity, string> = {
  error: 'E',
  warn: 'W',
  plain: '',
};

/** Read out before the line itself, so the tint is not colour-only. */
const SEVERITY_ANNOUNCE: Record<LineSeverity, string> = {
  error: 'Looks like an error: ',
  warn: 'Looks like a warning: ',
  plain: '',
};

interface LogLine {
  id: number;
  text: string;
  severity: LineSeverity;
}

type SeverityFilter = 'all' | 'flagged' | 'error';

/** What a started stream is pinned to. `nonce` makes "restart" a new target
 *  even when every other field is identical. */
interface StreamTarget {
  nonce: number;
  kind: 'container' | 'edge';
  name: string;
  tail: number;
  since: string;
  until: string;
  label: string;
}

export function LogsPage() {
  const sources = useResource<LogSourceListResponse>('/api/logs/sources');
  const [sourceId, setSourceId] = useState('');
  const [tail, setTail] = useState<number | string>(200);
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [active, setActive] = useState<StreamTarget | null>(null);
  const nonceRef = useRef(0);

  const { containers, edges } = useMemo(() => {
    const all = sources.data?.sources ?? [];
    return {
      containers: all.filter((s) => s.kind === 'container'),
      edges: all.filter((s) => s.kind === 'edge'),
    };
  }, [sources.data]);

  const source = sources.data?.sources.find((s) => s.id === sourceId) ?? null;
  const isEdge = source?.kind === 'edge';

  // Kept as typed rather than clamped on every keystroke, so the field can be
  // emptied mid-edit; the range is enforced at submit and stated inline.
  const tailNumber = typeof tail === 'number' ? tail : Number(tail);
  const tailValid =
    Number.isFinite(tailNumber) && tailNumber >= TAIL_MIN && tailNumber <= TAIL_MAX;
  const sinceValid = WINDOW_PATTERN.test(since.trim());
  const untilValid = WINDOW_PATTERN.test(until.trim());

  const canStart =
    Boolean(source) &&
    tailValid &&
    sinceValid &&
    untilValid;

  function start(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!source || !canStart) return;
    nonceRef.current += 1;
    setActive({
      nonce: nonceRef.current,
      kind: source.kind === 'edge' ? 'edge' : 'container',
      name: source.name || source.id,
      tail: Math.min(TAIL_MAX, Math.max(TAIL_MIN, Math.trunc(tailNumber))),
      since: since.trim(),
      until: until.trim(),
      label: source.label,
    });
  }

  const list = sources.data?.sources ?? [];
  const stale = Boolean(sources.error && sources.data);

  return (
    <>
      <PageHeader
        title="Logs"
        actions={
          <Freshness
            checkedAt={sources.fetchedAt}
            refreshing={sources.refreshing}
            onRefresh={sources.reload}
            label="Sources listed"
            staleAfterMs={600_000}
          />
        }
      />

      {stale ? <StaleBanner error={sources.error} onRetry={sources.reload} /> : null}

      {/* The edge registry could not be read (e.g. migration 024 not applied):
          say so, rather than let a missing "Edge nodes" group read as "none
          enrolled". The hint names a migration, never an address. */}
      {sources.data?.edge_unavailable_reason ? (
        <Callout tone="info" title="Edge nodes are not listed">
          {sources.data.edge_unavailable_reason}
        </Callout>
      ) : null}

      {sources.loading && !sources.data ? (
        <SkeletonRows rows={3} />
      ) : sources.error && !sources.data ? (
        <ErrorBlock
          what="the log sources"
          error={sources.error}
          onRetry={sources.reload}
        />
      ) : list.length === 0 ? (
        <EmptyBlock title="Nothing can be streamed from this controller">
          The controller lists no loggable container and no enrolled edge node. Log
          sources are derived from the fleet configuration and the edge-node registry,
          so this is a configuration state rather than a permissions problem.
        </EmptyBlock>
      ) : (
        <div className="logs-layout">
          {/* One row, above the tail rather than beside it. No CardHeader: the
              labels on the controls already say what this is, and a title plus
              a subtitle here cost two rows of the viewer's height. */}
          <Card className="logs-bar" aria-label="Choose a log source">
            <form className="logs-bar__form" onSubmit={start} noValidate>
              <Select
                id="log-source"
                labelText="Source"
                className="logs-bar__wide"
                value={sourceId}
                onChange={(event) => {
                  setSourceId(event.target.value);
                  // Changing the target invalidates the running tail rather
                  // than quietly leaving the previous source on screen under
                  // a new label.
                  setActive(null);
                }}
              >
                <SelectItem value="" text="Select a source…" />
                {containers.length > 0 ? (
                  <SelectItemGroup label="Containers">
                    {containers.map((s) => (
                      <SelectItem key={s.id} value={s.id} text={s.label} />
                    ))}
                  </SelectItemGroup>
                ) : null}
                {edges.length > 0 ? (
                  <SelectItemGroup label="Edge nodes">
                    {edges.map((s) => (
                      <SelectItem key={s.id} value={s.id} text={s.label} />
                    ))}
                  </SelectItemGroup>
                ) : null}
              </Select>

              <NumberInput
                id="log-tail"
                label="Tail"
                className="logs-bar__num"
                min={TAIL_MIN}
                max={TAIL_MAX}
                value={tail}
                invalid={!tailValid}
                invalidText={`${TAIL_MIN}–${formatNumber(TAIL_MAX)} lines.`}
                onChange={(_event, { value }: { value: number | string }) => setTail(value)}
              />

              <TextInput
                id="log-since"
                labelText="Since"
                className="logs-bar__num"
                placeholder="2h"
                value={since}
                invalid={!sinceValid}
                invalidText="Not a timestamp or an offset."
                onChange={(event) => setSince(event.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
              <TextInput
                id="log-until"
                labelText="Until"
                className="logs-bar__num"
                placeholder="30m"
                value={until}
                invalid={!untilValid}
                invalidText="Not a timestamp or an offset."
                onChange={(event) => setUntil(event.target.value)}
                autoComplete="off"
                spellCheck={false}
              />

              <div className="logs-bar__actions">
                <Button type="submit" size="sm" renderIcon={TerminalIcon} disabled={!canStart}>
                  {active ? 'Restart' : 'Stream'}
                </Button>
                {active ? (
                  <Button kind="tertiary" size="sm" onClick={() => setActive(null)}>
                    Stop
                  </Button>
                ) : null}
              </div>
            </form>

            {/* The two facts a wrong value here costs you, and nothing else. */}
            <p className="logs-bar__note">
              <code>since</code>/<code>until</code> take docker&rsquo;s own grammar —
              RFC3339 (<code>2026-08-23T10:00:00Z</code>) or an offset (<code>2h</code>) —
              and narrow the <strong>replay</strong> only; the tail still follows new
              lines after it.
              {isEdge
                ? ' An edge stream runs over SSH to the node’s NetBird mesh address, which the server works out itself; the terminal must be enabled and the node’s host key trusted (on its node page).'
                : ''}
            </p>
          </Card>

          {active ? (
            <LogTail
              key={`${active.kind}:${active.name}:${active.tail}:${active.since}:${active.until}:${active.nonce}`}
              target={active}
              onStop={() => setActive(null)}
            />
          ) : (
            <Card aria-label="Log stream">
              <div className="logs-placeholder logs-placeholder--idle">
                <TerminalIcon size={22} />
                <p className="logs-placeholder__title">No stream running</p>
                <p>
                  Output goes straight to this browser and the controller keeps no copy,
                  so there is nothing to search or replay after the socket closes.
                </p>
              </div>
            </Card>
          )}
        </div>
      )}
    </>
  );
}

/* ── the tail ─────────────────────────────────────────────────────────────── */

type ConnState = 'connecting' | 'open' | 'closed' | 'error';

const CONN_LABEL: Record<ConnState, string> = {
  connecting: 'Connecting…',
  open: 'Streaming',
  closed: 'Ended',
  error: 'Error',
};

function ConnIndicator({ state }: { state: ConnState }) {
  return (
    <span className={`logs-conn logs-conn--${state}`} role="status">
      <span className="logs-conn__dot" aria-hidden="true" />
      {CONN_LABEL[state]}
    </span>
  );
}

/** Safe for a filename: the source name is a compose service or a node uuid,
 *  but it reaches the disk, so it is narrowed rather than trusted. */
function fileSlug(value: string): string {
  return value.replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'logs';
}

function LogTail({ target, onStop }: { target: StreamTarget; onStop: () => void }) {
  const [state, setState] = useState<ConnState>('connecting');
  const [detail, setDetail] = useState('');
  const [lines, setLines] = useState<readonly LogLine[]>([]);
  const [follow, setFollow] = useState(true);
  const [severity, setSeverity] = useState<SeverityFilter>('all');
  // Edge only: where the server decided to connect, from the ticket.
  const [address, setAddress] = useState<string | null>(null);

  const viewRef = useRef<HTMLDivElement>(null);
  const queueRef = useRef<LogLine[]>([]);
  const partialRef = useRef('');
  const seqRef = useRef(0);

  const { kind, name, tail, since, until, nonce } = target;

  useEffect(() => {
    let disposed = false;
    let socket: WebSocket | null = null;
    let timer: number | null = null;

    const flush = () => {
      timer = null;
      if (disposed) return;
      const queued = queueRef.current;
      if (queued.length === 0) return;
      queueRef.current = [];
      setLines((prev) => {
        const next = prev.length === 0 ? queued : [...prev, ...queued];
        return next.length > MAX_LINES ? next.slice(next.length - MAX_LINES) : next;
      });
    };

    const schedule = () => {
      if (timer !== null || disposed) return;
      timer = window.setTimeout(flush, FLUSH_MS);
    };

    const push = (text: string) => {
      seqRef.current += 1;
      queueRef.current.push({
        id: seqRef.current,
        text,
        severity: classify(text),
      });
    };

    // Frames are byte chunks, not lines: a message can end mid-line and the
    // next one continues it. The remainder is held rather than rendered as a
    // line of its own, which would split one log entry into two.
    const ingest = (chunk: string) => {
      const parts = (partialRef.current + chunk.replace(/\r\n?/g, '\n')).split('\n');
      partialRef.current = parts.pop() ?? '';
      for (const part of parts) push(part);
      if (parts.length > 0) schedule();
    };

    /** The socket ended: whatever is left in the partial buffer is a whole line
     *  now, because nothing more is coming. */
    const drain = () => {
      const rest = partialRef.current;
      partialRef.current = '';
      if (rest) push(rest);
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
      flush();
    };

    async function begin() {
      let ticket: LogTicketResponse;
      const body: LogTicketRequest = { kind, name, tail, since, until };
      try {
        ticket = await apiPost<LogTicketResponse>('/api/logs/ticket', body);
      } catch (cause) {
        if (!disposed) {
          setState('error');
          setDetail(describeError(cause));
        }
        return;
      }
      if (disposed) return;
      setAddress(ticket.address ?? null);

      socket = new WebSocket(websocketUrl(ticket.ws_path));
      socket.onopen = () => {
        if (!disposed) setState('open');
      };
      socket.onmessage = (event) => {
        if (!disposed) ingest(String(event.data));
      };
      socket.onerror = () => {
        if (disposed) return;
        setState('error');
        setDetail('the connection failed — the source may be down');
      };
      socket.onclose = (event) => {
        if (disposed) return;
        setState((prev) => (prev === 'error' ? prev : 'closed'));
        setDetail(event.reason ? event.reason.trim() : `closed (code ${event.code})`);
        drain();
      };
    }

    void begin();

    return () => {
      disposed = true;
      if (timer !== null) window.clearTimeout(timer);
      try {
        socket?.close();
      } catch {
        /* already closing */
      }
    };
  }, [kind, name, tail, since, until, nonce]);

  const counts = useMemo(() => {
    let error = 0;
    let warn = 0;
    for (const line of lines) {
      if (line.severity === 'error') error += 1;
      else if (line.severity === 'warn') warn += 1;
    }
    return { error, warn, all: lines.length };
  }, [lines]);

  const shown = useMemo(() => {
    if (severity === 'all') return lines;
    if (severity === 'error') return lines.filter((l) => l.severity === 'error');
    return lines.filter((l) => l.severity !== 'plain');
  }, [lines, severity]);

  // Autoscroll is a layout effect so the jump happens in the same paint as the
  // new lines, rather than as a visible lurch after them.
  useLayoutEffect(() => {
    if (!follow) return;
    const view = viewRef.current;
    if (view) view.scrollTop = view.scrollHeight;
  }, [shown, follow]);

  /** Scrolling away from the bottom pauses the follow; scrolling back resumes
   *  it. A tail that yanks you back down while you are reading is unusable. */
  const handleScroll = useCallback(() => {
    const view = viewRef.current;
    if (!view) return;
    const atBottom = view.scrollHeight - view.scrollTop - view.clientHeight < 24;
    setFollow((prev) => (prev === atBottom ? prev : atBottom));
  }, []);

  const clear = useCallback(() => {
    queueRef.current = [];
    partialRef.current = '';
    setLines([]);
  }, []);

  const download = useCallback(() => {
    // The client's buffer, verbatim — the whole of it, not the filtered view.
    const text = lines.map((line) => line.text).join('\n');
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const blob = new Blob([text ? `${text}\n` : ''], {
      type: 'text/plain;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${fileSlug(target.kind)}-${fileSlug(target.name)}-${stamp}.log`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }, [lines, target.kind, target.name]);

  const jumpToLatest = useCallback(() => {
    const view = viewRef.current;
    if (view) view.scrollTop = view.scrollHeight;
    setFollow(true);
  }, []);

  return (
    <Card flush aria-label={`Log stream: ${target.label}`}>
      <CardHeader
        title={target.label}
        subtitle={
          <span className="logs-facts">
            <span className="logs-facts__item">
              {target.kind === 'edge' ? 'Edge node' : 'Container'}{' '}
              <span className="logs-facts__value">{target.name}</span>
            </span>
            {address ? (
              <span className="logs-facts__item">
                at <span className="logs-facts__value">{address}</span>
              </span>
            ) : null}
            <span className="logs-facts__item">
              tail <span className="logs-facts__value">{formatNumber(target.tail)}</span>
            </span>
            <span className="logs-facts__item">
              since{' '}
              <span className="logs-facts__value">{target.since || 'unbounded'}</span>
            </span>
            <span className="logs-facts__item">
              until{' '}
              <span className="logs-facts__value">{target.until || 'unbounded'}</span>
            </span>
          </span>
        }
        actions={<ConnIndicator state={state} />}
      />

      <div className="logs-toolbar">
        <SegmentedFilter<SeverityFilter>
          legend="Filter the lines on screen by their guessed severity"
          value={severity}
          onChange={setSeverity}
          options={[
            { value: 'all', label: 'All', count: counts.all },
            { value: 'flagged', label: 'Flagged', count: counts.error + counts.warn },
            { value: 'error', label: 'Errors', count: counts.error },
          ]}
        />
        <div className="logs-toolbar__end">
          <span className="logs-count">
            {severity === 'all'
              ? `${formatNumber(counts.all)} ${pluralise(counts.all, 'line')}`
              : `${formatNumber(shown.length)} of ${formatNumber(counts.all)}`}
          </span>
          {/* The switch is the live-tail state: on = following, off = paused.
              The label stays put rather than renaming itself on toggle — a
              control whose accessible name changes under you is announced as a
              different control. `aria-checked` and the Jump button carry the
              state instead. */}
          <Toggle
            id="log-follow"
            labelText="Follow tail"
            toggled={follow}
            onToggle={setFollow}
          />
          {/* Two copies, because they are two different things and conflating
              them is how a bug report arrives with the evidence filtered out.
              Both are produced lazily: joining four thousand lines on every
              render to fill a prop nobody pressed is a waste. */}
          <Tooltip
            side="bottom"
            label={
              severity === 'all'
                ? 'The lines on screen.'
                : 'Only the lines the severity filter is showing.'
            }
          >
            <CopyButton
              text={() => shown.map((line) => line.text).join('\n')}
              label="Copy visible"
              disabled={shown.length === 0}
            />
          </Tooltip>
          <Tooltip
            side="bottom"
            label={`Everything this browser still holds — the newest ${formatNumber(MAX_LINES)} lines, unfiltered.`}
          >
            <CopyButton
              text={() => lines.map((line) => line.text).join('\n')}
              label="Copy all loaded"
              disabled={counts.all === 0}
            />
          </Tooltip>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={DownloadIcon}
            disabled={counts.all === 0}
            onClick={download}
            title="Save the lines this browser is holding. The controller keeps no copy."
          >
            Download
          </Button>
          {/* The only control here that destroys something. Secondary, not
              danger: it drops a local buffer, it does not touch the source. */}
          <Button
            kind="secondary"
            size="sm"
            className="logs-clear"
            renderIcon={TrashIcon}
            disabled={counts.all === 0}
            onClick={clear}
          >
            Clear
          </Button>
          <Button kind="tertiary" size="sm" onClick={onStop}>
            Stop
          </Button>
        </div>
      </div>

      {state === 'error' && detail ? (
        <div className="logs-alert">
          <Callout tone="critical" title="The stream stopped" live>
            {detail}
          </Callout>
        </div>
      ) : null}

      <div className="logs-viewport">
        {/* A scrollable region has to be focusable to be scrollable from the
            keyboard. aria-live is deliberately off: a tail announced line by
            line is a firehose no screen reader user can escape — the connection
            state carries role="status" instead, which is the part that changes
            meaningfully. */}
        <div
          className="logs-view"
          ref={viewRef}
          onScroll={handleScroll}
          role="region"
          aria-label={`Log output from ${target.label}`}
          aria-live="off"
          tabIndex={0}
        >
          {shown.length > 0 ? (
            <ol className="logs-lines">
              {shown.map((line) => (
                <li key={line.id} className={`logs-line logs-line--${line.severity}`}>
                  <span className="logs-line__mark" aria-hidden="true">
                    {SEVERITY_MARK[line.severity]}
                  </span>
                  <span className="logs-line__text">
                    {line.severity === 'plain' ? null : (
                      <span className="visually-hidden">
                        {SEVERITY_ANNOUNCE[line.severity]}
                      </span>
                    )}
                    {line.text === '' ? ' ' : line.text}
                  </span>
                </li>
              ))}
            </ol>
          ) : counts.all > 0 ? (
            <div className="logs-placeholder">
              <p className="logs-placeholder__title">Nothing in this buffer matched</p>
              <p>
                None of the {formatNumber(counts.all)}{' '}
                {pluralise(counts.all, 'line')} held here looks like{' '}
                {severity === 'error' ? 'an error' : 'a warning or an error'}. The match
                is a text guess, so a real problem worded differently will not be caught
                by it.
              </p>
              <Button kind="tertiary" size="sm" onClick={() => setSeverity('all')}>
                Show all lines
              </Button>
            </div>
          ) : state === 'connecting' ? (
            <div className="logs-placeholder">
              <LoadingBlock label="Minting a ticket and opening the socket…" />
            </div>
          ) : state === 'open' ? (
            <div className="logs-placeholder">
              <p className="logs-placeholder__title">Connected — nothing has arrived yet</p>
              <p>
                {target.since || target.until
                  ? 'The source has emitted nothing inside the replay window, and nothing new since. The tail is still following.'
                  : 'The source has emitted nothing yet. The tail is still following.'}
              </p>
            </div>
          ) : (
            <div className="logs-placeholder">
              <p className="logs-placeholder__title">The stream ended with no output</p>
              {detail ? <p>{detail}</p> : null}
            </div>
          )}
        </div>

        {!follow && shown.length > 0 ? (
          <Button
            className="logs-jump"
            kind="secondary"
            size="sm"
            renderIcon={ArrowDownIcon}
            onClick={jumpToLatest}
          >
            Jump to latest
          </Button>
        ) : null}
      </div>

      {state === 'closed' && detail && counts.all > 0 ? (
        <p className="logs-footnote">Stream ended: {detail}</p>
      ) : null}

      {/* Both sentences are load-bearing, so neither is cut: the first stops the
          tint being read as a level field the controller sent, the second stops
          Copy and Download being read as a complete record. */}
      <p className="logs-footnote">
        Severity is guessed in this browser by matching the line text — the controller
        sends no level field — so the tint is a highlight, not a fact. This view holds
        the newest {formatNumber(MAX_LINES)} lines and drops older ones; Copy and
        Download give exactly that, because nothing is retained controller-side.
      </p>
    </Card>
  );
}
